import React, { useContext, useEffect, useRef, useState } from "react";
import "../employeesModals/addEmployeeModal.css"; // External CSS file
import cancelIcon from "../../../../assets/images/cancelArrow.svg";
import { Field, FieldArray, Form, Formik } from "formik";
import { notifySuccess, notifyWarn } from "../../../../assets/elements/Toast";
import * as Yup from "yup";
import CustomErrorMessage from "../../../../assets/common/CustomeErrorMessage";
import MainServices from "../../../../services/MainServices";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";
import ButtonLoading from "../../../../assets/loaders/ButtonLoading";
import styles from "./addModal.module.css";

export default function AddReligionModal({
  onClose,
  handleOnSubmit,
  editData,
  getTableData,
  handleOnUpdate,
}) {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const { updateReligionData, addReligionData } = MainServices();
  const [loading, setLoading] = useState(false);

  const getValidationSchema = () => {
    return Yup.object().shape({
      religionName: Yup.string()
        .trim()
        .required("Religion Name is required")
        .min(3, "Religion Name must be at least 3 characters")
        .max(50, "Religion Name must not exceed 50 characters")
        .matches(
          /^[A-Za-z0-9\u0600-\u06FF][A-Za-z0-9\u0600-\u06FF\s.,/-]*[A-Za-z0-9\u0600-\u06FF]$/,
          "Religion Name must start and end with a letter or number, and can include spaces and basic punctuation"
        )
        .matches(
          /^(?!.*[\s]{2,})(?!.*[.,/-]{2,}).*$/,
          "Consecutive spaces or punctuation are not allowed"
        )
        .matches(
          /[A-Za-z\u0600-\u06FF]/,
          "Religion Name must include at least one letter"
        ),

      description: Yup.string()
        .trim()
        .max(255, "Description must not exceed 255 characters"),
    });
  };

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleSubmit = async (values, resetForm) => {
    const details = {
      name: values?.religionName,
      description: values?.description,
    };
    try {
      setLoading(true);
      const response = await addReligionData(details);

      if (response?.data) {
        handleOnSubmit();
        getTableData();
        resetForm();
      }
    } catch (error) {
      notifyWarn(
        error?.response?.data?.message || "An unexpected error occurred"
      );
      console.error("Error creating designation", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (values, resetForm) => {
    const details = {
      name: values?.religionName,
      description: values?.description,
    };

    try {
      setLoading(true);
      const response = await updateReligionData(
        details,
        editData?.publicId,
        editData?.concurrencyStamp
      );

      if (response?.data) {
        handleOnUpdate();
        getTableData();
        resetForm();
      } else {
        notifyWarn("Failed to update designation");
      }
    } catch (error) {
      notifyWarn(
        error?.response?.data?.message || "An unexpected error occurred"
      );
      console.error("Error updating religion info", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles["add-modal-overlay"]} onClick={onClose}>
        <Formik
          initialValues={{
            religionName: editData?.name || "",
            description: editData?.description || "",
          }}
          validationSchema={getValidationSchema()}
          onSubmit={(values, { resetForm }) => {
            if (editData) {
              handleUpdate(values, resetForm);
            } else {
              handleSubmit(values, resetForm);
            }
          }}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form
              onClick={(e) => e.stopPropagation()}
              className={styles["add-modal-content"]}
            >
              <div className={styles["add-modal-header"]}>
                <span className={styles["add-modal-title"]}>
                  {editData ? t("Edit Religion") : t("Add New Religion")}
                </span>
                <div
                  className={styles["add-modal-icon-container"]}
                  onClick={onClose}
                >
                  <img src={cancelIcon} alt="cancel icon" />
                </div>
              </div>
              <div className={styles["add-subheader"]}></div>

              <div
                className={styles["add-body-content-flex"]}
                style={{ padding: "0px 24px" }}
              >
                <div className={styles["form-group"]}>
                  <div className={styles["add-body-input-section"]}>
                    <div className={styles["add-label-container"]}>
                      <label
                        className={styles["add-label"]}
                        htmlFor="religionName"
                      >
                        {t("Religion Name")}
                      </label>
                      <span className={styles["add-req-mark"]}>*</span>
                    </div>
                    <input
                      id="religionName"
                      name="religionName"
                      className={styles["add-input"]}
                      placeholder={t("Enter Religion Name")}
                      value={values?.religionName}
                      onChange={(e) =>
                        setFieldValue("religionName", e.target.value)
                      }
                    />
                  </div>
                  <CustomErrorMessage
                    name="religionName"
                    errors={errors}
                    touched={touched}
                  />
                </div>

                <div className={styles["form-group"]}>
                  <div className={styles["add-body-input-section"]}>
                    <div className={styles["add-label-container"]}>
                      <label
                        className={styles["add-label"]}
                        htmlFor="description"
                      >
                        {t("Description")}
                      </label>
                    </div>
                    <textarea
                      id="description"
                      name="description"
                      className={styles["add-input-textarea"]}
                      placeholder={t("Enter Description Name")}
                      value={values?.description}
                      onChange={(e) =>
                        setFieldValue("description", e.target.value)
                      }
                    />
                  </div>
                  <CustomErrorMessage
                    name="description"
                    errors={errors}
                    touched={touched}
                  />
                </div>
              </div>

              <div className={styles["add-footer"]}>
                <span></span>
                <div className={styles["add-footer-right"]}>
                  <button
                    type="button"
                    className={styles["add-footer-cancel-btn"]}
                    onClick={onClose}
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    disabled={loading}
                    type="submit"
                    className={styles["add-footer-submit-btn"]}
                  >
                    {loading ? <ButtonLoading /> : t("Submit")}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
}
